import { AfterViewInit, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { Chart, FontSpec } from 'chart.js';

import { TreemapDataPoint, TreemapScriptableContext } from 'chartjs-chart-treemap';
import { getColorByDataIndex } from 'app/utils/chart.utils';
import { Base64Image } from 'app/utils/pdf.utils';
import { IChartValuePoint } from 'app/models/citation-charts.models';

type CitationsCountDataPoint = TreemapDataPoint & { _data: IChartValuePoint };

@Component({
  selector: 'app-treemap-chart',
  templateUrl: './treemap-chart.component.html',
  styleUrls: ['./treemap-chart.component.scss'],
})
export class TreemapChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('treemapCanvas') treemapCanvas!: {
    nativeElement: HTMLCanvasElement;
  };

  private citationCountsInternal: IChartValuePoint[] = [];

  @Input() set citationCounts(value: IChartValuePoint[]) {
    this.citationCountsInternal = value
      .map((citation) =>
        citation.label
          ? citation
          : {
              ...citation,
              citationType: 'N/A',
            },
      )
      .sort((a, b) => b.count - a.count);
    this.updateChartData();
  }

  get citationsCounts(): IChartValuePoint[] {
    return this.citationCountsInternal;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chart: Chart<any, any, any> | undefined;

  readonly NAME_FONT_SIZE = 16;
  readonly VALUE_FONT_SIZE = 19;

  ngAfterViewInit(): void {
    const canvas: HTMLCanvasElement = this.treemapCanvas.nativeElement;
    const context: CanvasRenderingContext2D | null | undefined = canvas?.getContext('2d');
    if (context) {
      this.createTreemapChart(context);
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  createTreemapChart(context: CanvasRenderingContext2D): void {
    this.chart = new Chart(context, {
      type: 'treemap',
      data: {
        datasets: [
          {
            data: this.citationsCounts,
            tree: this.citationsCounts as [],
            key: 'count',
            label: 'Citations count',
            labels: {
              display: true,
              position: 'top',
              align: 'left',
              color: 'white',
              font: () => [{ size: this.NAME_FONT_SIZE }, { size: this.VALUE_FONT_SIZE }] as FontSpec[],
              formatter: (ctx) => {
                const value = (ctx.raw as CitationsCountDataPoint).v.toString();
                const name = this.getEllipsizedName(ctx) ?? '';
                return [name, '', value];
              },
              overflow: 'fit',
            },
            backgroundColor: (ctx: TreemapScriptableContext) => this.colorFromRaw(ctx),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: () => '',
              label: (item) => {
                // eslint-disable-next-line no-underscore-dangle
                const citationName = (item.raw as CitationsCountDataPoint)._data?.label;
                const count = (item.raw as CitationsCountDataPoint).v.toString();
                return [` Type:   ${citationName}`, ` Count: ${count}`];
              },
            },
          },
        },
      },
    });
  }

  private updateChartData() {
    this.chart?.update();
  }

  getEllipsizedName(ctx: TreemapScriptableContext) {
    // eslint-disable-next-line no-underscore-dangle
    const name = (ctx.raw as CitationsCountDataPoint)._data?.label;
    const pixelRatio = window.devicePixelRatio;

    if (!name) {
      return undefined;
    }

    const nameWidth = (name.length * this.NAME_FONT_SIZE) / pixelRatio;
    const elementWidth = ctx.raw.w;

    if (nameWidth >= elementWidth + 20) {
      const length = (elementWidth * pixelRatio) / this.NAME_FONT_SIZE;
      return name.slice(0, Math.max(0, length - 3)).concat('...');
    } else {
      return name;
    }
  }

  colorFromRaw(ctx: TreemapScriptableContext) {
    if (ctx.type !== 'data') {
      return 'transparent';
    }
    return getColorByDataIndex(ctx.dataIndex);
  }

  exportToImage(): Base64Image | null {
    const width = this.chart?.width ?? 0;
    const height = this.chart?.height ?? 0;
    const imageData = this.chart?.toBase64Image();
    if (imageData) {
      return { imageData, width, height };
    }
    return null;
  }
}
